import { useEffect, useState } from "react";
import { useOrder } from "../../context/OrderContext";
import { nextBulkTier } from "../../lib/pricingConfig";
import api from "../../lib/api";
import { toast } from "sonner";
import errorMessage from "../../lib/errors";
import { Truck, Wrench, Ruler, Loader2, Store, TrendingUp } from "lucide-react";
import { useActivePromotion } from "../PromoBanner";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const DEFAULT_UPSELLS = {
  pro_measurement: false,
  delivery: false,
  delivery_install: false,
  pickup: true, // default fulfillment is free pickup
};

export default function StepUpsell() {
  const { state, update, goNext, goTo } = useOrder();
  const [u, setU] = useState({
    ...DEFAULT_UPSELLS,
    white_glove: !!state.whiteGlove, // preserve pre-checkout decision
  });
  const [quote, setQuote] = useState(null);
  const [serviceArea, setServiceArea] = useState(null);
  const [saving, setSaving] = useState(false);
  // When the customer adds *paid* upsells AFTER their base order is paid,
  // we render an inline Stripe Embedded Checkout for just the delta.
  const [upsellCheckout, setUpsellCheckout] = useState(null); // {client_secret, session_id, amount, items}

  const isWindow = state.product?.type === "window";
  const petCount = !isWindow && state.petscreenAll ? state.quantity : 0;
  const meshType = isWindow ? (state.meshType || "standard") : "standard";
  const whiteGloveLocked = !!state.whiteGlove; // hide Pro Measurement when white_glove already chosen

  // Post-checkout "complete the bundle" upsell. Same per-screen price applies
  // to the additional screens, so the customer sees big savings without us
  // changing any pricing math.
  const bundleTier = isWindow ? nextBulkTier(state.quantity) : null;
  const [bundleSaving, setBundleSaving] = useState(false);
  const [bundleAdded, setBundleAdded] = useState(null); // {added, saved} after success

  const addBundle = async () => {
    if (!bundleTier || !state.orderId) return;
    setBundleSaving(true);
    try {
      const newQty = state.quantity + bundleTier.screensAway;
      const r = await api.patch(`/orders/${state.orderId}/quantity`, { quantity: newQty });
      const added = bundleTier.screensAway;
      const saved = bundleTier.dollarOff;
      update({ quantity: r.data.quantity, items: r.data.items || [] });
      setBundleAdded({ added, saved });
      toast.success(`Added ${added} more screens — you saved $${saved}!`);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't extend the order. Try again."));
    } finally {
      setBundleSaving(false);
    }
  };

  useEffect(() => {
    const z = state.customer?.zip_code;
    if (z) {
      api.get(`/service-area?zip=${encodeURIComponent(z)}`)
        .then((r) => setServiceArea(r.data)).catch(() => setServiceArea(null));
    }
  }, [state.customer?.zip_code]);

  useEffect(() => {
    if (!state.product) return;
    api.post("/quote", {
      product_slug: state.product.slug,
      quantity: state.quantity,
      petscreen_count: petCount,
      mesh_type: meshType,
      upsells: u,
      state: state.customer?.state || null,
    }).then((r) => setQuote(r.data));
  }, [u, state.product, state.quantity, petCount, meshType, state.customer?.state]);

  // Mutually exclusive fulfillment: pickup / delivery / delivery_install
  const setFulfillment = (kind) => {
    setU((prev) => ({
      ...prev,
      pickup: kind === "pickup",
      delivery: kind === "delivery",
      delivery_install: kind === "delivery_install",
    }));
  };

  const togglePro = () => setU((prev) => ({ ...prev, pro_measurement: !prev.pro_measurement }));

  const finalize = async (selected) => {
    if (!state.orderId) return;
    setSaving(true);
    try {
      // Always preserve white_glove if it was locked in pre-checkout.
      const finalUpsells = { ...selected, white_glove: selected.white_glove || whiteGloveLocked };
      const r = await api.post(`/orders/${state.orderId}/upsells`, {
        upsells: finalUpsells,
        origin_url: window.location.origin,
      });
      // New post-checkout flow: server returns a Stripe Embedded Checkout
      // session that covers JUST the new paid upsells. Render it inline; the
      // useEffect below polls until Stripe confirms, then advances the funnel.
      if (r.data?.requires_payment) {
        setUpsellCheckout({
          client_secret: r.data.client_secret,
          session_id: r.data.session_id,
          amount: r.data.amount,
          items: r.data.items,
        });
        return;
      }
      // Pre-checkout (legacy) or free-only change: server returned the order
      // doc directly.
      const order = r.data;
      update({
        upsells: finalUpsells,
        measurementsRequired: order?.measurements_required,
      });
      goTo(order?.measurements_required ? "measurements" : "done");
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  // Poll for the upsell payment to clear once the embedded checkout is up.
  useEffect(() => {
    if (!upsellCheckout?.session_id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/orders/${state.orderId}/upsell-status`,
          { params: { session_id: upsellCheckout.session_id } });
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          toast.success("Upsells confirmed!");
          setUpsellCheckout(null);
          // Re-fetch the order to get the new upsells/pricing then advance.
          try {
            const ord = await api.get(`/orders/${state.orderId}`);
            update({
              upsells: ord.data.upsells,
              measurementsRequired: ord.data.measurements_required,
            });
            goTo(ord.data.measurements_required ? "measurements" : "done");
          } catch { goTo("done"); }
        }
      } catch { /* swallow — keep polling */ }
    };
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [upsellCheckout?.session_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const inZone = !!serviceArea?.in_zone;
  const pickupAddress = serviceArea?.pickup?.address || "Our shop";
  const pickupHours = serviceArea?.pickup?.hours || "";

  // Compact tile component — smaller than the old marketing card.
  const { promotion } = useActivePromotion();
  // Use the latest quote's threshold-met flag to decide whether to show
  // FREE badges on eligible tiles. Falls back to a local check against the
  // promo's `threshold_amount` against the *baseline* product subtotal so
  // the badge appears even before the customer adds the upsell.
  const promoSubtotalMet = !!(
    promotion?.active &&
    promotion?.type === "free_upsell" &&
    quote?.promotion?.threshold_met
  );
  const isPromoFreeKey = (key) => {
    if (!promoSubtotalMet) return false;
    return (promotion.free_upsell_keys || []).includes(key);
  };

  const Tile = ({ id, icon, title, price, hint, active, onClick, disabled, promoKey }) => {
    const isPromoFree = promoKey ? isPromoFreeKey(promoKey) : false;
    return (
    <button
      type="button"
      data-testid={`upsell-${id}`}
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-left rounded-2xl border-2 px-4 py-3 transition ${
        active
          ? "border-[var(--brand-green)] bg-[var(--brand-green-light)]"
          : "border-[var(--brand-border)] bg-white hover:border-[var(--brand-green)]/50"
      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      <div className="flex items-center gap-3">
        <div className="shrink-0">{icon}</div>
        <div className="flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="font-semibold text-[var(--brand-text)] text-sm flex items-center gap-1.5 flex-wrap">
              {title}
              {isPromoFree && (
                <span
                  data-testid={`promo-free-badge-${promoKey}`}
                  className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                  style={{ background: "#166534", color: "#fff" }}
                >Free with promo</span>
              )}
            </p>
            <p className="text-sm font-semibold muted-text">
              {isPromoFree ? <span className="brand-text">FREE</span> : price}
            </p>
          </div>
          {hint && <p className="text-[11px] muted-text mt-0.5 leading-snug">{hint}</p>}
        </div>
      </div>
    </button>
    );
  };

  return (
    <div data-testid="step-upsell">
      <div className="step-pill mb-3" style={{ background: "#dcfce7", color: "#166534" }}>
        Order paid · {state.orderNumber}
      </div>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-1 text-[var(--brand-text)]">
        How would you like to get it?
      </h1>
      <p className="muted-text text-sm mb-4">
        Pickup is free. Add delivery or install only if you need a hand.
      </p>

      {/* Bundle extension — once if the customer is below the top bulk tier. */}
      {bundleTier && !bundleAdded && (
        <button
          type="button"
          data-testid="upsell-bundle-nudge"
          onClick={addBundle}
          disabled={bundleSaving}
          className="w-full mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border-2 border-dashed border-[var(--brand-green)] bg-[var(--brand-green-light)]/40 hover:bg-[var(--brand-green-light)] transition-colors text-left disabled:opacity-60"
        >
          <span className="flex items-center gap-3 min-w-0">
            <TrendingUp className="w-5 h-5 brand-text shrink-0" />
            <span className="min-w-0">
              <span className="block font-bold brand-text text-sm">
                Add {bundleTier.screensAway} more screens · save ${bundleTier.dollarOff.toFixed(0)}
              </span>
              <span className="block text-[11px] muted-text leading-snug">
                Same per-screen price. We&apos;ll collect the extra measurements with the rest.
              </span>
            </span>
          </span>
          <span className="text-xs font-semibold brand-text shrink-0">
            {bundleSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tap to add →"}
          </span>
        </button>
      )}

      {bundleAdded && (
        <div
          data-testid="upsell-bundle-added"
          className="w-full mb-4 px-4 py-3 rounded-2xl border-2 brand-text"
          style={{ background: "#ecfdf5", borderColor: "#6ee7b7" }}
        >
          <p className="font-bold text-sm">
            +{bundleAdded.added} screens added · you saved ${bundleAdded.saved}
          </p>
          <p className="text-[11px] muted-text mt-0.5">
            Your total is now {state.quantity} screens. We&apos;ll collect measurements next.
          </p>
        </div>
      )}


      {/* Fulfillment tiles (mutually exclusive) */}
      <div className="space-y-2">
        <Tile
          id="pickup"
          icon={<Store className="w-5 h-5 brand-text" />}
          title="Pickup at our shop"
          price="FREE"
          hint={`${pickupAddress}${pickupHours ? ` · ${pickupHours}` : ""}`}
          active={u.pickup}
          onClick={() => setFulfillment("pickup")}
        />
        {inZone && (
          <Tile
            id="delivery"
            icon={<Truck className="w-5 h-5 brand-text" />}
            title="Delivery"
            price="+$39"
            hint="We deliver to your door."
            active={u.delivery}
            onClick={() => setFulfillment("delivery")}
            promoKey="delivery"
          />
        )}
        {inZone && (
          <Tile
            id="delivery-install"
            icon={<Wrench className="w-5 h-5 brand-text" />}
            title="Delivery + Installation"
            price="+$99"
            hint="We deliver and professionally install."
            active={u.delivery_install}
            onClick={() => setFulfillment("delivery_install")}
            promoKey="delivery_install"
          />
        )}
        {!inZone && state.customer?.zip_code && (
          <p className="text-[11px] muted-text px-1" data-testid="out-of-zone-note">
            Delivery & install aren&apos;t available for your ZIP — pickup only.
          </p>
        )}
      </div>

      {/* Optional add-on (less promotional) — hidden when white-glove is already locked-in */}
      {!whiteGloveLocked && (
        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-wider muted-text mb-2">Optional add-on</p>
          <Tile
            id="pro-measurement"
            icon={<Ruler className="w-5 h-5 brand-text" />}
            title="Professional measurement"
            price="+$39"
            hint="A pro confirms measurements at your home. You skip the measuring step."
            active={u.pro_measurement}
            onClick={togglePro}
            promoKey="pro_measurement"
          />
        </div>
      )}

      {whiteGloveLocked && (
        <p className="mt-4 text-[11px] muted-text px-1" data-testid="upsell-wg-locked">
          Perfect Fit Guarantee already added at checkout.
        </p>
      )}

      {quote && (
        <div className="mt-5 rounded-2xl border border-[var(--brand-border)] bg-white p-3 text-sm">
          <div className="flex justify-between mb-1">
            <span className="muted-text">Items</span>
            <span>${quote.items_subtotal.toFixed(2)}</span>
          </div>
          {quote.pro_measurement_fee > 0 && (
            <div className="flex justify-between mb-1">
              <span className="muted-text">Pro measurement</span>
              <span>+${quote.pro_measurement_fee.toFixed(2)}</span>
            </div>
          )}
          {quote.delivery_fee > 0 && (
            <div className="flex justify-between mb-1">
              <span className="muted-text">Delivery</span>
              <span>+${quote.delivery_fee.toFixed(2)}</span>
            </div>
          )}
          {quote.install_fee > 0 && (
            <div className="flex justify-between mb-1">
              <span className="muted-text">Delivery + Install</span>
              <span>+${quote.install_fee.toFixed(2)}</span>
            </div>
          )}
          {quote.white_glove_fee > 0 && (
            <div className="flex justify-between mb-1">
              <span className="muted-text">Perfect Fit Guarantee</span>
              <span data-testid="upsell-wg-fee">+${quote.white_glove_fee.toFixed(2)}</span>
            </div>
          )}
          {quote.bulk_discount > 0 && (
            <div className="flex justify-between mb-1 brand-text font-semibold">
              <span>Bulk Savings</span>
              <span data-testid="upsell-bulk">−${quote.bulk_discount.toFixed(2)}</span>
            </div>
          )}
          {quote.tax > 0 && (
            <div className="flex justify-between mb-1">
              <span className="muted-text">Sales tax ({(quote.tax_rate * 100).toFixed(2)}% · {quote.tax_state})</span>
              <span data-testid="upsell-tax">+${quote.tax.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t border-[var(--brand-border)] my-2" />
          <div className="flex justify-between font-bold text-base text-[var(--brand-text)]">
            <span>Total</span>
            <span data-testid="upsell-total">${quote.total.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Inline embedded checkout for paid upsells (delta only) */}
      {upsellCheckout && stripePromise && (
        <div className="mt-6 rounded-2xl overflow-hidden border-2 border-[var(--brand-green)]" data-testid="upsell-embedded-checkout">
          <div className="px-4 py-3 brand-bg text-white text-sm font-semibold">
            Pay ${upsellCheckout.amount.toFixed(2)} for: {upsellCheckout.items.map(i => i.label).join(", ")}
          </div>
          <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: upsellCheckout.client_secret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
          <div className="px-4 py-2 bg-amber-50 text-amber-900 text-xs">
            <button
              data-testid="upsell-cancel-checkout"
              onClick={() => setUpsellCheckout(null)}
              className="underline"
            >Cancel</button> — your upsells won't be added if you don't complete payment.
          </div>
        </div>
      )}

      {/* Equal-prominence CTAs */}
      <div className={`flex gap-3 mt-6 ${upsellCheckout ? "hidden" : ""}`}>
        <button
          data-testid="skip-upsell-btn"
          type="button"
          onClick={() => finalize({ ...DEFAULT_UPSELLS })}
          disabled={saving}
          className="flex-1 py-3 rounded-full border-2 border-[var(--brand-border)] font-semibold"
        >
          Just pickup
        </button>
        <button
          data-testid="confirm-upsell-btn"
          className="flex-1 py-3 rounded-full font-semibold border-2"
          style={{ borderColor: "var(--brand-green)", color: "var(--brand-green)", background: "white" }}
          disabled={saving}
          onClick={() => finalize(u)}
        >
          {saving
            ? <span className="inline-flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Saving</span>
            : "Continue"}
        </button>
      </div>
    </div>
  );
}
